import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  Legend, LabelList,
} from 'recharts';
import {
  ArrowLeft, RefreshCw, Plus, X, TrendingUp, TrendingDown,
  Building2, Users, FileText, Briefcase, Save, RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyItem = (prefix = 'item') => ({
  id: `${prefix}_${Math.random().toString(36).slice(2, 9)}`,
  label: '',
  amount: 0,
});

const fmt = (n, currency = 'PKR') => {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  return `${sign}${currency} ${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const sum = (arr) => arr.reduce((a, b) => a + (Number(b.amount) || 0), 0);

const MetricCard = ({ label, value, sub, tone = 'slate', big }) => {
  const tones = {
    slate: 'border-slate-200 dark:border-slate-700',
    emerald: 'border-emerald-300 bg-emerald-50/50 dark:bg-emerald-900/20',
    amber: 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/20',
    indigo: 'border-indigo-300 bg-indigo-50/50 dark:bg-indigo-900/20',
    slateDark: 'border-slate-500 bg-slate-100 dark:bg-slate-800',
    rose: 'border-rose-300 bg-rose-50/50 dark:bg-rose-900/20',
  };
  return (
    <div className={`border rounded-xl p-4 ${tones[tone] || tones.slate}`}>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className={`mt-2 font-bold ${big ? 'text-3xl' : 'text-2xl'} text-slate-900 dark:text-slate-50`}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{sub}</div>}
    </div>
  );
};

const LineList = ({ title, subtitle, items, autoItems = [], onChange, onAdd, onRemove, accent, currency }) => (
  <Card className="border-slate-200 dark:border-slate-700">
    <CardContent className="p-5 space-y-3">
      <div>
        <div className={`text-sm font-semibold text-slate-900 dark:text-slate-100 border-b-2 pb-1 inline-block ${accent}`}>
          {title}
        </div>
        {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
      </div>
      {/* Auto-computed lines — pulled live from CRM, not editable */}
      {autoItems.length > 0 && (
        <div className="space-y-2 pb-2 border-b border-dashed border-slate-200 dark:border-slate-700">
          {autoItems.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-2 bg-emerald-50/70 dark:bg-emerald-900/20 rounded-lg px-2 py-1.5 border border-emerald-200/60 dark:border-emerald-800/50"
              title={`Auto-synced from ${item.source || 'CRM'} for ${item.period || 'this month'}`}
            >
              <div className="flex-1 text-xs text-slate-700 dark:text-slate-200">
                <span className="inline-block px-1.5 py-0.5 mr-1 rounded bg-emerald-500 text-white text-[10px] font-semibold tracking-wide align-middle">
                  LIVE
                </span>
                {item.label}
              </div>
              <div className="w-32 text-right text-sm font-semibold text-emerald-700 dark:text-emerald-400 tabular-nums">
                {fmt(item.amount, currency)}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-2">
            <Input
              value={item.label}
              onChange={(e) => onChange(item.id, { label: e.target.value })}
              placeholder="Line item…"
              className="flex-1 h-9 text-sm"
              data-testid={`pnl-item-label-${item.id}`}
            />
            <Input
              type="number"
              value={item.amount}
              onChange={(e) => onChange(item.id, { amount: Number(e.target.value) || 0 })}
              className="w-32 h-9 text-right text-sm"
              data-testid={`pnl-item-amount-${item.id}`}
            />
            <button
              onClick={() => onRemove(item.id)}
              className="text-slate-400 hover:text-rose-500 p-1"
              aria-label="Remove line"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={onAdd}
        className="w-full text-xs text-slate-500 hover:text-teal-600 dark:hover:text-teal-400 border-2 border-dashed rounded-lg py-2"
      >
        <Plus className="w-3 h-3 inline mr-1" /> Add line
      </button>
      <div className="pt-2 border-t text-sm text-slate-600 dark:text-slate-400 flex justify-between">
        <span>Subtotal</span>
        <span className="font-semibold text-slate-800 dark:text-slate-100">
          {fmt(sum(items) + sum(autoItems), currency)}
        </span>
      </div>
    </CardContent>
  </Card>
);

const StatBadge = ({ icon: Icon, label, value, hint }) => (
  <div className="flex flex-col justify-between h-full border rounded-xl p-4 bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-700">
    <div className="flex items-center justify-between">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <Icon className="w-4 h-4 text-slate-400" />
    </div>
    <div className="text-3xl font-bold text-slate-900 dark:text-slate-50 mt-3">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

export default function PnLCalculatorPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowed, setAllowed] = useState(null);
  const [currency, setCurrency] = useState('PKR');
  const [revenue, setRevenue] = useState([]);
  const [variable, setVariable] = useState([]);
  const [fixed, setFixed] = useState([]);
  const [autoRevenue, setAutoRevenue] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsAt, setStatsAt] = useState(null);
  const [meta, setMeta] = useState(null);
  const debounceRef = useRef(null);

  const token = localStorage.getItem('token');
  const hdrs = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/pnl/config`, { headers: hdrs }),
        axios.get(`${API}/api/pnl/live-stats`, { headers: hdrs }),
      ]);
      const cfg = cfgRes.data || {};
      setCurrency(cfg.currency || 'PKR');
      setRevenue(cfg.revenue_items || []);
      setVariable(cfg.variable_items || []);
      setFixed(cfg.fixed_items || []);
      setAutoRevenue(cfg.auto_revenue_items || []);
      setMeta({ updated_at: cfg.updated_at, updated_by_name: cfg.updated_by_name });
      setStats(statsRes.data);
      setStatsAt(new Date());
      setAllowed(true);
    } catch (err) {
      if (err.response?.status === 403) {
        setAllowed(false);
      } else {
        toast.error('Failed to load P&L data');
      }
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleReset = async () => {
    if (!window.confirm('Zero out every amount on the P&L (revenue, variable, fixed)?\n\nLine labels stay. Consultancy fees auto-sync from leads is unaffected. This clears the shared company config for everyone.')) return;
    try {
      await axios.post(`${API}/api/pnl/reset`, {}, { headers: hdrs });
      toast.success('All amounts reset to 0');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    }
  };

  // Debounced auto-save whenever the user edits a line
  const scheduleSave = useCallback((next) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const res = await axios.put(`${API}/api/pnl/config`, next, { headers: hdrs });
        setMeta({ updated_at: res.data.updated_at, updated_by_name: res.data.updated_by_name });
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Save failed');
      } finally {
        setSaving(false);
      }
    }, 700);
  }, [token]); // eslint-disable-line

  const commit = (nextRevenue = revenue, nextVar = variable, nextFixed = fixed, nextCurrency = currency) => {
    scheduleSave({
      currency: nextCurrency,
      revenue_items: nextRevenue,
      variable_items: nextVar,
      fixed_items: nextFixed,
    });
  };

  const patchItem = (setter, list, id, patch) => {
    const next = list.map((it) => (it.id === id ? { ...it, ...patch } : it));
    setter(next);
    return next;
  };
  const addTo = (setter, list, prefix) => {
    const next = [...list, emptyItem(prefix)];
    setter(next);
    return next;
  };
  const removeFrom = (setter, list, id) => {
    const next = list.filter((it) => it.id !== id);
    setter(next);
    return next;
  };

  const totalRevenue = useMemo(() => sum(revenue) + sum(autoRevenue), [revenue, autoRevenue]);
  const totalVariable = useMemo(() => sum(variable), [variable]);
  const totalFixed = useMemo(() => sum(fixed), [fixed]);
  const contribution = totalRevenue - totalVariable;
  const netProfit = contribution - totalFixed;
  const contribMarginPct = totalRevenue > 0 ? (contribution / totalRevenue) * 100 : 0;
  const netMarginPct = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

  const chartData = [
    { name: 'Revenue', v: totalRevenue, fill: '#0d9488' },
    { name: 'Variable', v: totalVariable, fill: '#f59e0b' },
    { name: 'Fixed', v: totalFixed, fill: '#1e293b' },
    { name: 'Net profit', v: netProfit, fill: netProfit >= 0 ? '#10b981' : '#e11d48' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-slate-500">Loading P&L Calculator…</div>
      </div>
    );
  }

  if (allowed === false) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 gap-4">
        <div className="text-2xl font-semibold text-slate-800 dark:text-slate-100">Restricted</div>
        <div className="text-slate-500 max-w-md text-center">
          The P&L Calculator is available only to a small set of senior staff.
          If you believe you should have access, please contact the super admin.
        </div>
        <Button onClick={() => navigate('/admin')} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-16" data-testid="pnl-calculator-page">
      {/* Hero banner */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/admin')}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10 -ml-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
              </Button>
            </div>
            <div className="mt-2">
              <div className="text-amber-400 font-bold text-2xl tracking-wide">H E Direct</div>
              <div className="text-slate-300 text-sm">Monthly profit &amp; loss calculator</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/g96w8w8c_HE%20Direct%20logo%202023.png"
              alt="H E Direct logo"
              className="h-16 w-auto bg-white rounded-lg p-2 ring-2 ring-white/20 shadow-lg object-contain"
              data-testid="pnl-hed-logo"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 -mt-6">
        {/* Live CRM badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBadge icon={Briefcase} label="Active Agents" value={stats?.active_agents ?? '—'} />
          <StatBadge icon={FileText} label="Applications Total" value={stats?.applications_total ?? '—'} />
          <StatBadge icon={Users} label="Leads (Sept 2026)" value={stats?.leads_total ?? '—'} />
          <StatBadge icon={Building2} label="Employees" value={stats?.employees ?? '—'} />
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
          <span>
            Synced with CRM at {statsAt ? statsAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleReset} disabled={loading} data-testid="pnl-reset-btn" className="text-rose-600 border-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40">
              <RotateCcw className="w-3 h-3 mr-1" /> Reset to Zero
            </Button>
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="pnl-sync-btn">
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Sync with CRM
            </Button>
          </div>
        </div>

        {/* KPI ribbon */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
          <MetricCard tone="emerald" label="Total revenue" value={fmt(totalRevenue, currency)} big />
          <MetricCard tone="amber" label="Variable costs" value={fmt(totalVariable, currency)} big />
          <MetricCard
            tone="indigo"
            label="Contribution margin"
            value={fmt(contribution, currency)}
            sub={`${contribMarginPct.toFixed(1)}% of revenue`}
            big
          />
          <MetricCard tone="slateDark" label="Fixed costs" value={fmt(totalFixed, currency)} big />
          <MetricCard
            tone={netProfit >= 0 ? 'emerald' : 'rose'}
            label={netProfit >= 0 ? 'Net profit' : 'Net loss'}
            value={fmt(netProfit, currency)}
            sub={`${Math.abs(netMarginPct).toFixed(1)}% margin`}
            big
          />
        </div>

        {/* 3 columns of inputs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
          <LineList
            title="Revenue"
            subtitle="Money coming in — consultancy fees auto-sync from CRM leads"
            items={revenue}
            autoItems={autoRevenue}
            currency={currency}
            accent="border-emerald-500 text-emerald-700 dark:text-emerald-400"
            onChange={(id, patch) => {
              const next = patchItem(setRevenue, revenue, id, patch);
              commit(next, variable, fixed);
            }}
            onAdd={() => {
              const next = addTo(setRevenue, revenue, 'rev');
              commit(next, variable, fixed);
            }}
            onRemove={(id) => {
              const next = removeFrom(setRevenue, revenue, id);
              commit(next, variable, fixed);
            }}
          />
          <LineList
            title="Variable expenses"
            subtitle="Scale with student / agent volume"
            items={variable}
            currency={currency}
            accent="border-amber-500 text-amber-700 dark:text-amber-400"
            onChange={(id, patch) => {
              const next = patchItem(setVariable, variable, id, patch);
              commit(revenue, next, fixed);
            }}
            onAdd={() => {
              const next = addTo(setVariable, variable, 'var');
              commit(revenue, next, fixed);
            }}
            onRemove={(id) => {
              const next = removeFrom(setVariable, variable, id);
              commit(revenue, next, fixed);
            }}
          />
          <LineList
            title="Fixed expenses"
            subtitle="Same regardless of volume"
            items={fixed}
            currency={currency}
            accent="border-slate-800 dark:border-slate-300 text-slate-800 dark:text-slate-100"
            onChange={(id, patch) => {
              const next = patchItem(setFixed, fixed, id, patch);
              commit(revenue, variable, next);
            }}
            onAdd={() => {
              const next = addTo(setFixed, fixed, 'fix');
              commit(revenue, variable, next);
            }}
            onRemove={(id) => {
              const next = removeFrom(setFixed, fixed, id);
              commit(revenue, variable, next);
            }}
          />
        </div>

        {/* Chart */}
        <Card className="mt-6 border-slate-200 dark:border-slate-700">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Profit &amp; loss snapshot
              </div>
              <div className="flex items-center gap-1 text-xs">
                {netProfit >= 0 ? (
                  <span className="flex items-center gap-1 text-emerald-600">
                    <TrendingUp className="w-3 h-3" /> Profitable
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-rose-600">
                    <TrendingDown className="w-3 h-3" /> Loss making
                  </span>
                )}
              </div>
            </div>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 30, right: 30, left: 10, bottom: 10 }}>
                  <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 12 }} tickFormatter={(v) => v.toLocaleString()} />
                  <Tooltip
                    formatter={(v) => fmt(v, currency)}
                    contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: 'white' }}
                  />
                  <Legend />
                  <Bar dataKey="v" name={currency} radius={[6, 6, 0, 0]}>
                    <LabelList
                      dataKey="v"
                      position="top"
                      formatter={(v) => fmt(v, currency)}
                      style={{ fill: '#334155', fontSize: 11, fontWeight: 600 }}
                    />
                    {chartData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Footer / save indicator */}
        <div className="mt-6 flex flex-wrap items-center justify-between text-xs text-slate-500 gap-2">
          <div>
            <strong className="text-slate-700 dark:text-slate-200">H E Direct</strong> — Peshawar · Islamabad · P&amp;L Calculator
          </div>
          <div className="flex items-center gap-2">
            {saving ? (
              <span className="flex items-center gap-1 text-teal-600">
                <Save className="w-3 h-3 animate-pulse" /> Saving…
              </span>
            ) : (
              <span>
                Last saved by <strong>{meta?.updated_by_name || 'system'}</strong>
                {meta?.updated_at && (
                  <> at {new Date(meta.updated_at).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}</>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
